// src/Pages/Pay/PayPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Button, Typography, TextField, Autocomplete, Divider, Stepper, Step, StepLabel,
  Container, Paper, Backdrop, CircularProgress, Snackbar, FormControlLabel, Chip, Alert,
  Stack, Avatar, Card, CardContent, Fade, Grow, Collapse, LinearProgress, Skeleton,
  IconButton, InputAdornment, useMediaQuery, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Radio,
} from '@mui/material';
import {
  People, Payment, CheckCircle, ArrowBack, ArrowForward, Person, AttachMoney, Delete,
  Email, Email as EmailIcon, Public as GlobalIcon,
  Search as SearchIcon, Lock as LockIcon, Business as BusinessIcon, AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon, Info as InfoIcon, AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon, CurrencyBitcoin as CryptoIcon, ErrorOutline as ErrorOutlineIcon,
  EditNote as EditNoteIcon, ExpandMoreRounded as ExpandMoreIcon, ExpandLessRounded as ExpandLessIcon,
  TuneRounded as TuneIcon, Bolt as BoltIcon, UploadFile as UploadFileIcon,
  TableChart as TableChartIcon, CheckCircleOutline as CheckCircleOutlineIcon,
  Download as DownloadIcon, Close as CloseIcon,
} from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import client from '../../API';
import { getPONumber } from '../../Utils/constants';
import useAuth from '../../Hooks/use-auth';

const NOTES_MIN_LENGTH = 10;

// ─── FIX: strip commas/spaces/$ before parsing so "50,000" → 50000 ──────────
const parseAmt = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const cleaned = String(val).replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

// ─── Derive a display name from the email's first segment + random digits ───
//     jane.smith@example.com → "Jane4827"
const nameFromEmail = (email) => {
  const local = String(email || '').split('@')[0] || 'user';
  const first = local.split(/[._\-+]/)[0] || 'user';
  const pretty = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${pretty}${rand}`;
};

const AlertComponent = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const RAILS = [
  { id: 'paypal', label: 'PayPal', shortLabel: 'PayPal', Icon: Payment, color: '#003087', bgColor: '#f0f4ff', borderColor: '#c7d2fe', feePercent: 3.0, feeLabel: '3% transfer fee', eta: 'Instant', badge: null, notes: 'Works for any vendor — no platform account required.', requiresPlatformAccount: false },
  { id: 'stripe', label: 'Stripe', shortLabel: 'Stripe', Icon: CreditCardIcon, color: '#635bff', bgColor: '#f5f3ff', borderColor: '#ddd6fe', feePercent: 0, feeLabel: null, eta: 'Instant', badge: 'Partner', notes: 'Instant payouts via our Stripe partnership. No transfer fee.', requiresPlatformAccount: true },
  { id: 'bank_ach', label: 'ACH / Bank', shortLabel: 'ACH', Icon: AccountBalanceIcon, color: '#2563eb', bgColor: '#eff6ff', borderColor: '#bfdbfe', feePercent: 0, feeLabel: null, eta: '1–3 business days', badge: null, notes: 'US bank accounts only. No extra transfer fee.', requiresPlatformAccount: true },
];
const getRailById = (id) => RAILS.find((r) => r.id === id) || RAILS[0];

const getWalletIcon  = (t) => ({ paypal: <Payment sx={{ fontSize: 16 }} />, stripe: <CreditCardIcon sx={{ fontSize: 16 }} />, wise: <WalletIcon sx={{ fontSize: 16 }} />, bank_ach: <AccountBalanceIcon sx={{ fontSize: 16 }} />, crypto: <CryptoIcon sx={{ fontSize: 16 }} /> }[t] || <WalletIcon sx={{ fontSize: 16 }} />);
const getWalletColor = (t) => ({ paypal: 'info', stripe: 'secondary', wise: 'success', bank_ach: 'primary', crypto: 'warning' }[t] || 'default');
const getWalletLabel = (t) => ({ paypal: 'PayPal', stripe: 'Stripe', wise: 'Wise', bank_ach: 'Bank/ACH', crypto: 'Crypto' }[t] || t);

const WalletBadges = ({ walletTypes, legacyMethods, hasModernWallets, size = 'small', sx = {} }) => {
  if (!walletTypes?.length && !legacyMethods?.length)
    return <Chip label="No payment method" size={size} icon={<WarningIcon sx={{ fontSize: 14 }} />} color="error" variant="outlined" sx={{ fontSize: '0.7rem', height: size === 'small' ? 20 : 24, ...sx }} />;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, ...sx }}>
      {walletTypes?.map((t) => <Chip key={t} label={getWalletLabel(t)} size={size} icon={getWalletIcon(t)} color={getWalletColor(t)} variant="outlined" sx={{ fontSize: '0.7rem', height: size === 'small' ? 20 : 24 }} />)}
      {!hasModernWallets && legacyMethods?.map((m) => <Chip key={m} label={`Legacy: ${m}`} size={size} color="default" variant="outlined" sx={{ fontSize: '0.7rem', height: size === 'small' ? 20 : 24, opacity: 0.7 }} />)}
    </Stack>
  );
};

const SectionCard = ({ icon, title, children, accent = false, sx = {} }) => (
  <Box sx={{ borderRadius: '14px', border: '1.5px solid', borderColor: accent ? 'primary.200' : 'grey.200', bgcolor: accent ? alpha('#1976d2', 0.025) : 'white', overflow: 'hidden', mb: 2.5, ...sx }}>
    {(icon || title) && (
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: 1.75, borderBottom: '1px solid', borderColor: accent ? 'primary.100' : 'grey.100', display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: accent ? alpha('#1976d2', 0.04) : 'grey.50' }}>
        {icon && <Box sx={{ color: accent ? 'primary.main' : 'text.secondary', display: 'flex' }}>{icon}</Box>}
        {title && <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', md: '0.95rem' } }}>{title}</Typography>}
      </Box>
    )}
    <Box sx={{ p: { xs: 2.5, md: 3 } }}>{children}</Box>
  </Box>
);

const FeePayerCards = ({ value, onChange, meLabel, meSub, creatorLabel, creatorSub, accentColor = 'primary' }) => {
  const theme = useTheme();
  const color = { primary: theme.palette.primary.main, warning: theme.palette.warning.main }[accentColor] || theme.palette.primary.main;
  return (
    <Stack spacing={1.5}>
      {[{ value: 'me', label: meLabel, sub: meSub }, { value: 'creator', label: creatorLabel, sub: creatorSub }].map((opt) => (
        <Card key={opt.value} variant="outlined" onClick={() => onChange(opt.value)}
          sx={{ borderRadius: 2.5, cursor: 'pointer', borderColor: value === opt.value ? color : 'divider', bgcolor: value === opt.value ? alpha(color, 0.04) : 'transparent', borderWidth: value === opt.value ? 2 : 1, transition: 'all 0.18s', '&:hover': { borderColor: value === opt.value ? color : 'grey.400' } }}>
          <CardContent sx={{ p: { xs: 1.75, md: 2 }, '&:last-child': { pb: { xs: 1.75, md: 2 } } }}>
            <FormControlLabel value={opt.value} control={<Radio checked={value === opt.value} sx={{ color, '&.Mui-checked': { color } }} />}
              label={<Box><Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{opt.label}</Typography><Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>{opt.sub}</Typography></Box>}
              sx={{ m: 0, width: '100%', pointerEvents: 'none' }} />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

// ─── HORIZONTAL TICKER ────────────────────────────────────────────────────────
const TICKER_PHRASES = [
  '✂️  Pay a clipper', '🎬  Pay an editor', '🎨  Pay a designer', '📣  Send to a creator',
  '🔧  Pay a contractor', '💻  Pay a developer', '🏢  Settle a vendor', '📸  Pay a photographer',
  '✍️  Pay a copywriter', '🎙️  Pay a voice actor', '📊  Pay a consultant', '🎵  Pay a musician',
];

const SearchTicker = () => {
  const items = [...TICKER_PHRASES, ...TICKER_PHRASES];
  return (
    <Box sx={{ overflow: 'hidden', position: 'relative', mb: 2,
      '&::before': { content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: 48, zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to right, white, transparent)' },
      '&::after':  { content: '""', position: 'absolute', top: 0, bottom: 0, right: 0, width: 48, zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to left, white, transparent)' },
    }}>
      <Box sx={{ display: 'flex', width: 'max-content', animation: 'tickerScroll 40s linear infinite',
        '@keyframes tickerScroll': { '0%': { transform: 'translateX(0)' }, '100%': { transform: 'translateX(-50%)' } },
        '&:hover': { animationPlayState: 'paused' },
      }}>
        {items.map((phrase, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box sx={{ px: 1.75, py: 0.55, borderRadius: 5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50', mx: 0.6,
              transition: 'all 0.18s', '&:hover': { bgcolor: alpha('#1976d2', 0.06), borderColor: alpha('#1976d2', 0.3) } }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.77rem', color: 'text.secondary' }}>{phrase}</Typography>
            </Box>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300', mx: 0.4, flexShrink: 0 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── CSV BANNER ───────────────────────────────────────────────────────────────
const ROTATING_WORDS = [
  { word: 'new vendors', emoji: '🏢' }, { word: 'clippers', emoji: '✂️' },
  { word: 'editors', emoji: '🎬' },     { word: 'designers', emoji: '🎨' },
  { word: 'developers', emoji: '💻' },  { word: 'influencers', emoji: '📣' },
  { word: 'contractors', emoji: '🔧' }, { word: 'people', emoji: '👥' },
];

const CSVBanner = ({ onUploadClick }) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliding, setSliding] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setSliding(true);
      setTimeout(() => { setCurrentIndex((p) => (p + 1) % ROTATING_WORDS.length); setSliding(false); }, 300);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  const current = ROTATING_WORDS[currentIndex];
  return (
    <Box onClick={onUploadClick} sx={{
      borderRadius: 2.5, border: '2px dashed', borderColor: alpha(theme.palette.primary.main, 0.3),
      bgcolor: alpha(theme.palette.primary.main, 0.025), cursor: 'pointer', overflow: 'hidden',
      transition: 'all 0.2s ease', position: 'relative',
      '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.05), transform: 'translateY(-1px)', boxShadow: `0 4px 18px ${alpha(theme.palette.primary.main, 0.1)}`, '& .csv-cta': { bgcolor: 'primary.dark' } },
      '&::after': { content: '""', position: 'absolute', top: 0, left: '-60%', width: '50%', height: '100%', background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.4)}, transparent)`, animation: 'shine 3.8s ease-in-out infinite', pointerEvents: 'none' },
      '@keyframes shine': { '0%': { left: '-60%' }, '55%': { left: '130%' }, '100%': { left: '130%' } },
    }}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.5, md: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 }, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <TableChartIcon sx={{ fontSize: { xs: 20, md: 24 }, color: 'primary.main' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', mb: 0.2, overflow: 'hidden', height: { xs: 24, md: 26 } }}>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.82rem', md: '0.88rem' }, whiteSpace: 'nowrap' }}>Paying multiple</Typography>
            <Box sx={{ overflow: 'hidden', height: { xs: 24, md: 26 }, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ transform: sliding ? 'translateY(-110%)' : 'translateY(0)', opacity: sliding ? 0 : 1, transition: sliding ? 'transform 0.26s cubic-bezier(0.55,0,1,0.45),opacity 0.2s' : 'transform 0.28s cubic-bezier(0,0.55,0.45,1),opacity 0.22s' }}>
                <Box component="span" sx={{ px: 1, py: 0.15, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.12), border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`, color: 'primary.main', fontWeight: 800, fontSize: { xs: '0.8rem', md: '0.86rem' }, whiteSpace: 'nowrap' }}>
                  {current.emoji} {current.word}
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.82rem', md: '0.88rem' }, whiteSpace: 'nowrap' }}>?</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', md: '0.77rem' } }}>
            Upload a CSV — just email + amount (description optional) — and import the whole list at once
          </Typography>
        </Box>
        <Box className="csv-cta" sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.6, px: 1.75, py: 0.85, borderRadius: 2, bgcolor: 'primary.main', color: 'white', flexShrink: 0, transition: 'background 0.18s' }}>
          <UploadFileIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.76rem', whiteSpace: 'nowrap' }}>Upload CSV</Typography>
          <ArrowForward sx={{ fontSize: 12 }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── CSV PARSER ───────────────────────────────────────────────────────────────
const splitCSVLine = (line) => {
  const result = []; let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  result.push(cur.trim()); return result;
};

// Email-only CSV. Columns: Email (required), Amount (required), Description (optional).
// Display names are auto-generated from the email.
const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], errors: ['CSV needs a header row and at least one data row.'] };
  const rawHeaders = splitCSVLine(lines[0]);
  const headers = rawHeaders.map((h) => h.toLowerCase().replace(/['"]/g, '').trim());
  const col = (kws) => headers.findIndex((h) => kws.some((k) => h.includes(k)));
  const emailIdx  = col(['email']);
  const amountIdx = col(['amount', 'pay', 'price', '$']);
  const descIdx   = col(['description', 'desc', 'note', 'memo']);
  const errors = [];
  if (emailIdx === -1) errors.push('Missing "email" column.');
  if (amountIdx === -1) errors.push('Missing "amount" column.');
  if (errors.length) return { rows: [], errors };
  const rows = [], rowErrors = [];
  lines.slice(1).forEach((line, i) => {
    if (!line.trim()) return;
    const cols = splitCSVLine(line);
    const email  = (cols[emailIdx]  || '').replace(/^"|"$/g,'').trim();
    const rawAmt = (cols[amountIdx] || '').replace(/^"|"$/g,'').trim();
    const desc   = descIdx !== -1 ? (cols[descIdx] || '').replace(/^"|"$/g,'').trim() : '';
    // FIX: strip commas/$ so "50,000" parses correctly
    const amount = rawAmt ? parseFloat(rawAmt.replace(/[$,\s]/g,'')) : NaN;
    const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!validEmail) { rowErrors.push(`Row ${i+2}: invalid or missing email.`); return; }
    rows.push({
      name: nameFromEmail(email), // auto-generated: first part of email + random digits
      contact: email,
      contactType: 'email',
      amount: (!isNaN(amount) && amount > 0) ? amount.toString() : '',
      description: desc,
      needsAmount: isNaN(amount) || amount <= 0,
    });
  });
  return { rows, errors: rowErrors };
};

// ─── CSV UPLOAD MODAL ─────────────────────────────────────────────────────────
const CSVUploadModal = ({ open, onClose, onImportAndAdvance, showSnackbar }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [isDragOver,  setIsDragOver]  = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [editedRows,  setEditedRows]  = useState([]);
  const [fileName,    setFileName]    = useState('');

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.match(/\.csv$/i) && file.type !== 'text/csv') { showSnackbar('Please upload a .csv file.', 'error'); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => { const r = parseCSV(e.target.result); setParseResult(r); setEditedRows(r.rows.map((row) => ({ ...row }))); };
    reader.readAsText(file);
  }, [showSnackbar]);

  const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); handleFile(e.dataTransfer.files[0]); };
  const updateRowAmount = (i, val) => setEditedRows((prev) => prev.map((r, idx) => idx === i ? { ...r, amount: val, needsAmount: false } : r));

  const handleConfirm = () => {
    const invalid = editedRows.filter((r) => !r.amount || parseAmt(r.amount) <= 0);
    if (invalid.length) { showSnackbar(`Fill in amounts for: ${invalid.map((r) => r.contact).join(', ')}.`, 'warning'); return; }
    onImportAndAdvance(editedRows);
    setParseResult(null); setEditedRows([]); setFileName(''); onClose();
  };
  const handleClose = () => { setParseResult(null); setEditedRows([]); setFileName(''); onClose(); };

  const downloadTemplate = () => {
    const csv = 'Email,Amount,Description\njane@example.com,150,Campaign deliverables\njohn@example.com,200,Editing invoice\nalex@studio.io,75.50,Design work\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'blitzpay_template.csv'; a.click(); URL.revokeObjectURL(url);
  };

  // FIX: use parseAmt throughout for consistent comma-safe parsing
  const totalReady   = editedRows.filter((r) => r.amount && parseAmt(r.amount) > 0).length;
  const totalMissing = editedRows.filter((r) => !r.amount || parseAmt(r.amount) <= 0).length;
  const grandTotal   = editedRows.reduce((acc, r) => acc + parseAmt(r.amount), 0);
  const parseErrors  = parseResult?.errors || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'grey.200', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, white 100%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TableChartIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Bulk CSV Import</Typography>
              <Typography variant="caption" color="text.secondary">{fileName || 'Add many payees at once'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 14 }} />} onClick={downloadTemplate} sx={{ fontSize: '0.75rem', textTransform: 'none', fontWeight: 700 }}>Template</Button>
            <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {!parseResult && (
          <Box sx={{ p: 3 }}>
            <Box onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
              sx={{ border: '2px dashed', borderColor: isDragOver ? 'primary.main' : alpha(theme.palette.primary.main, 0.3), borderRadius: 3, bgcolor: isDragOver ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.02), p: { xs: 5, md: 7 }, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => handleFile(e.target.files[0])} />
              <Box sx={{ width: 64, height: 64, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, transform: isDragOver ? 'scale(1.1) rotate(-4deg)' : 'none', transition: 'transform 0.2s' }}>
                <UploadFileIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>{isDragOver ? 'Drop it!' : 'Drop your CSV here'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.85rem' }}>or <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>click to browse</Box></Typography>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', display: 'inline-block', textAlign: 'left' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', fontSize: '0.66rem', letterSpacing: '0.08em' }}>Expected columns</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {['Email', 'Amount', 'Description (optional)'].map((col) => (
                    <Chip key={col} label={col} size="small" sx={{ fontSize: '0.72rem', height: 22, bgcolor: 'white', fontWeight: 600, fontFamily: 'monospace', border: '1px solid', borderColor: 'grey.300' }} />
                  ))}
                </Box>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontSize: '0.68rem' }}>
                  Display names are generated automatically from each email
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {parseResult && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 100, p: 1.5, borderRadius: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: 'success.main', lineHeight: 1 }}>{totalReady}</Typography>
                <Typography variant="caption" color="success.dark" sx={{ fontWeight: 700 }}>Ready</Typography>
              </Box>
              {totalMissing > 0 && (
                <Box sx={{ flex: 1, minWidth: 100, p: 1.5, borderRadius: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: 'warning.main', lineHeight: 1 }}>{totalMissing}</Typography>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>Need amount</Typography>
                </Box>
              )}
              {parseErrors.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 100, p: 1.5, borderRadius: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: 'error.main', lineHeight: 1 }}>{parseErrors.length}</Typography>
                  <Typography variant="caption" color="error.dark" sx={{ fontWeight: 700 }}>Skipped</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 100, p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>${grandTotal.toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Total</Typography>
              </Box>
            </Box>

            {parseErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Rows skipped:</Typography>
                {parseErrors.slice(0,4).map((e,i) => <Typography key={i} variant="caption" sx={{ display: 'block' }}>• {e}</Typography>)}
                {parseErrors.length > 4 && <Typography variant="caption">…and {parseErrors.length-4} more</Typography>}
              </Alert>
            )}
            {totalMissing > 0 && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Fill in the missing amounts below to continue.</Typography></Alert>}

            {editedRows.length > 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Email','Name (auto)','Amount'].map((h) => <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50', color: 'text.secondary' }}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedRows.map((row, i) => (
                      <TableRow key={i} sx={{ bgcolor: !row.amount || parseAmt(row.amount) <= 0 ? 'warning.50' : 'transparent', '&:hover': { bgcolor: !row.amount ? 'warning.100' : 'grey.50' } }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <EmailIcon sx={{ fontSize: 13, color: 'info.main', flexShrink: 0 }} />
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.contact}</Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{row.name}</TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          {(!row.amount || parseAmt(row.amount) <= 0) ? (
                            <TextField size="small" type="number" placeholder="0.00" value={row.amount}
                              onChange={(e) => updateRowAmount(i, e.target.value)}
                              InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" fontWeight={700}>$</Typography></InputAdornment> }}
                              sx={{ width: 100, '& input': { fontSize: '0.8rem', py: 0.5 } }}
                              autoFocus={i === editedRows.findIndex((r) => !r.amount || parseAmt(r.amount) <= 0)} />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.83rem', color: 'success.main' }}>${parseAmt(row.amount).toFixed(2)}</Typography>
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={() => updateRowAmount(i, '')}><EditNoteIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button size="small" variant="text" startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                onClick={() => { setParseResult(null); setEditedRows([]); setFileName(''); }}
                sx={{ fontSize: '0.78rem', textTransform: 'none', color: 'text.secondary' }}>
                Upload a different file
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'grey.200', gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 90 }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!editedRows.length || totalMissing > 0} endIcon={<ArrowForward />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 230 }}>
          {totalMissing > 0
            ? `Fill in ${totalMissing} missing amount${totalMissing !== 1 ? 's' : ''}`
            : `Import ${editedRows.length} payee${editedRows.length !== 1 ? 's' : ''} & continue →`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const PayPage = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();

  const [step,            setStep]            = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [csvModalOpen,    setCSVModalOpen]    = useState(false);

  const [searchQuery,        setSearchQuery]        = useState('');
  const [searchResults,      setSearchResults]      = useState([]);
  const [searchPerformed,    setSearchPerformed]    = useState(false);
  const [searchType,         setSearchType]         = useState('');
  const [found,              setFound]              = useState(false);
  const [exactMatch,         setExactMatch]         = useState(null);
  const [showManualAddPanel, setShowManualAddPanel] = useState(false);
  const [payouts,            setPayouts]            = useState([]);
  // CHANGED: SMS/phone invite state removed — email invites only
  const [emailInviteName,    setEmailInviteName]    = useState('');
  const [inviteEmail,        setInviteEmail]        = useState('');
  const [emailInviteAmount,  setEmailInviteAmount]  = useState('');
  const [manualAddName,      setManualAddName]      = useState('');
  const [manualAddEmail,     setManualAddEmail]     = useState('');
  const [manualAddAmount,    setManualAddAmount]    = useState('');
  const [emailRecipients,    setEmailRecipients]    = useState([]);
  const [notes,              setNotes]              = useState('');
  const [notesTouched,       setNotesTouched]       = useState(false);
  const [isProcessing,       setIsProcessing]       = useState(false);
  const [spendingInfo,       setSpendingInfo]       = useState(null);
  const [spendingInfoLoading,setSpendingInfoLoading]= useState(true);
  const [companyUsers,       setCompanyUsers]       = useState([]);
  const [emailSearchInput,   setEmailSearchInput]   = useState('');
  const [isEmailSearching,   setIsEmailSearching]   = useState(false);
  const [errorOpen,          setErrorOpen]          = useState(false);
  const [feePayer,           setFeePayer]           = useState('creator');
  const [nominalInterestRate,setNominalInterestRate]= useState(10);
  const [showAdvancedRail,   setShowAdvancedRail]   = useState(false);
  const [forcedRail,         setForcedRail]         = useState(null);
  const [railFeePayer,       setRailFeePayer]       = useState('creator');
  const [snackbarOpen,       setSnackbarOpen]       = useState(false);
  const [snackbarMessage,    setSnackbarMessage]    = useState('');
  const [snackbarSeverity,   setSnackbarSeverity]   = useState('info');

  const steps = [
    { label: isMobile ? 'Payees'  : 'Add Payees',  icon: <People /> },
    { label: isMobile ? 'Fee'     : 'Fee Setup',    icon: <Payment /> },
    { label: isMobile ? 'Confirm' : 'Confirmation', icon: <CheckCircle /> },
  ];

  const { data: companyInfo, isLoading: companyInfoLoading, isError: companyInfoError } = useQuery(
    ['company', userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    { enabled: !!userInfo?.company_id, retry: 1 }
  );
  const hasBlitzPayAccess = companyInfo?.blitzpay_account === true;

  const { mutate: fetchSpendingInfo } = useMutation(() => client.teams.getMySpendingInfo(), {
    onSuccess: (data) => { setSpendingInfo(data); setNominalInterestRate(parseFloat(data.effective_interest_rate || data.company_interest_rate || data.team_interest_rate || 10)); setSpendingInfoLoading(false); },
    onError: () => { setSpendingInfo(null); setSpendingInfoLoading(false); },
  });

  const { mutate: searchCreatorForPayment, isLoading: isSearching } = useMutation(
    (query) => client.creators.searchForPayment({ query }),
    {
      onSuccess: (data) => {
        setSearchResults(data.results || []); setSearchType(data.search_type || ''); setExactMatch(data.exact_match || null); setFound(Boolean(data.found)); setSearchPerformed(true);
        if (!data.found) {
          setShowManualAddPanel(false);
          // CHANGED: email-only invites — phone searches no longer pre-fill an invite form
          if (data.search_type === 'username') { setEmailInviteName(data.query||''); setInviteEmail(''); setEmailInviteAmount(''); }
          else if (data.search_type === 'email') { setEmailInviteName(''); setInviteEmail(data.query||''); setEmailInviteAmount(''); }
        } else { setManualAddName(''); setManualAddEmail(''); setManualAddAmount(''); setShowManualAddPanel(true); }
      },
      onError: () => showSnackbar('Failed to search. Please try again.', 'error'),
    }
  );

  useEffect(() => { if (hasBlitzPayAccess) fetchSpendingInfo(); }, [hasBlitzPayAccess]);

  useEffect(() => {
    if (!hasBlitzPayAccess) { setCompanyUsers([]); setIsEmailSearching(false); return; }
    const q = (emailSearchInput||'').trim();
    if (q.length < 2) { setCompanyUsers([]); setIsEmailSearching(false); return; }
    const t = setTimeout(async () => {
      setIsEmailSearching(true);
      try { const res = await client.globalUsers.search({ q, limit: 20 }); const users = Array.isArray(res?.users) ? res.users : Array.isArray(res?.results) ? res.results : []; setCompanyUsers([...new Set(users.map((u) => u?.email).filter(Boolean))]); }
      catch { setCompanyUsers([]); } finally { setIsEmailSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [emailSearchInput, hasBlitzPayAccess]);

  const debouncedQuery = useDebounce(searchQuery.trim(), 300);
  useEffect(() => {
    if (debouncedQuery.length >= 2) { setSearchPerformed(false); setShowManualAddPanel(false); searchCreatorForPayment(debouncedQuery); }
    else { setSearchPerformed(false); setSearchResults([]); setFound(false); setExactMatch(null); setShowManualAddPanel(false); }
  }, [debouncedQuery]);

  const showSnackbar = (msg, severity = 'info') => { setSnackbarMessage(msg); setSnackbarSeverity(severity); setSnackbarOpen(true); };

  const getSpendingData = () => {
    if (!spendingInfo) return { isTeamSource: false, totalAvailable: 0, effectiveLimit: 0, teamUsed: 0, utilizationPercent: 0, hasNoFunds: true, hasLowFunds: false, isHealthy: false, sourceName: 'Unknown', companyLimit: 0 };
    const isTeamSource   = spendingInfo.spending_source === 'team';
    const totalAvailable = parseFloat(spendingInfo.total_available_funds || 0);
    const effectiveLimit = parseFloat(spendingInfo.team_allocated_spending || spendingInfo.effective_spending_limit || 0);
    const teamUsed       = parseFloat(spendingInfo.team_used_spending || 0);
    const companyLimit   = parseFloat(spendingInfo.company_spending_limit || 0);
    const sourceName     = isTeamSource ? (spendingInfo.primary_team_name || 'Team') : (spendingInfo.company_name || 'Company');
    return { isTeamSource, totalAvailable, effectiveLimit, teamUsed, utilizationPercent: effectiveLimit > 0 ? (teamUsed/effectiveLimit)*100 : 0, hasNoFunds: !spendingInfo.has_available_funds || totalAvailable === 0, hasLowFunds: totalAvailable > 0 && totalAvailable < 1000, isHealthy: totalAvailable >= 1000, sourceName, companyLimit };
  };
  const spendingData = getSpendingData();

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const allPayeesAreExistingUsers = payouts.length > 0 && payouts.every((p) => p.type === 'existing');
  const availableRails = RAILS.filter((r) => !r.requiresPlatformAccount || allPayeesAreExistingUsers);

  // FIX: all subtotal/total calculations now use parseAmt so commas don't truncate values
  const calculateSubtotal    = () => payouts.reduce((acc, p) => acc + parseAmt(p.amount), 0);
  const calculateBlitzPayFee = (amt) => amt * (nominalInterestRate/100);
  const selectedRail         = forcedRail ? getRailById(forcedRail) : null;
  const railHasFee           = selectedRail && selectedRail.feePercent > 0;
  const railFeeAmount        = railHasFee ? calculateSubtotal() * (selectedRail.feePercent/100) : 0;
  const senderCoversRailFee  = railHasFee && railFeePayer === 'me';
  const calculateTotal       = () => { let t = calculateSubtotal(); if (feePayer === 'me') t += t*(nominalInterestRate/100); if (senderCoversRailFee) t += railFeeAmount; return t; };
  const calculateReceiverAmount = () => { const s = calculateSubtotal(); let d = 0; if (feePayer !== 'me') d += s*(nominalInterestRate/100); if (railHasFee && railFeePayer === 'creator') d += s*(selectedRail.feePercent/100); return s-d; };
  const getUtilizationColor  = (pct) => pct >= 90 ? 'error' : pct >= 75 ? 'warning' : pct >= 50 ? 'info' : 'success';
  const methodMode           = forcedRail ? 'forced' : 'recipient_choice';
  const notesValid           = notes.trim().length >= NOTES_MIN_LENGTH;
  const notesInvalid         = notesTouched && !notesValid;

  useEffect(() => { if (forcedRail && !availableRails.find((r) => r.id === forcedRail)) setForcedRail(null); }, [availableRails.length]);

  const goToStep = (n) => { setIsTransitioning(true); setTimeout(() => { setStep(n); setIsTransitioning(false); }, 150); };
  const nextStep = () => step < steps.length - 1 && goToStep(step + 1);
  const prevStep = () => step > 0 && goToStep(step - 1);

  const handleAddExisting = (result) => {
    if (payouts.some((p) => p.type === 'existing' && p.data.creator === result.creator)) { showSnackbar('Creator already added.', 'warning'); return; }
    setPayouts((prev) => [...prev, { type: 'existing', data: result, amount: '', poNumber: getPONumber('TCC') }]);
    setSearchQuery(''); setSearchPerformed(false); setSearchResults([]); setFound(false); setExactMatch(null); setShowManualAddPanel(false);
  };

  // CHANGED: email-only invite path — SMS removed
  const handleAddNew = () => {
    const name  = emailInviteName.trim();
    const email = (searchType === 'email' ? debouncedQuery : inviteEmail.trim());
    const amount = emailInviteAmount;
    // FIX: use parseAmt so "50,000" is accepted as 50000
    if (!name || !isValidEmail(email) || !amount || parseAmt(amount) <= 0) { showSnackbar('Please complete all fields with valid values.', 'warning'); return; }
    const cleanedAmount = String(parseAmt(amount));
    setPayouts((prev) => [...prev, { type: 'new-email', data: { name, email }, amount: cleanedAmount, poNumber: `EMAIL-${Date.now()}` }]);
    setEmailInviteName(''); setInviteEmail(''); setEmailInviteAmount('');
    setSearchQuery(''); setSearchPerformed(false); setShowManualAddPanel(false);
  };

  // CHANGED: email-only manual add — SMS removed
  const handleManualAddFromQuery = () => {
    const name = manualAddName.trim(), amount = manualAddAmount;
    const email = (searchType === 'email' ? debouncedQuery : manualAddEmail.trim());
    // FIX: use parseAmt so "50,000" is accepted
    if (!name || !amount || parseAmt(amount) <= 0) { showSnackbar('Please enter a valid name and amount.', 'warning'); return; }
    if (!isValidEmail(email)) { showSnackbar('Enter a valid email address.', 'error'); return; }
    const cleanedAmount = String(parseAmt(amount));
    setPayouts((prev) => [...prev, { type: 'new-email', data: { name, email }, amount: cleanedAmount, poNumber: `EMAIL-${Date.now()}` }]);
    setManualAddName(''); setManualAddEmail(''); setManualAddAmount(''); setShowManualAddPanel(false); setSearchQuery(''); setSearchPerformed(false);
  };

  // CSV rows are always email invites
  const handleCSVImportAndAdvance = (rows) => {
    const existingContacts = new Set(payouts.map((p) => p.data?.email||'').filter(Boolean));
    const newPayouts = rows.map((row) => ({
      type: 'new-email',
      data: { name: row.name, email: row.contact },
      // Store cleaned numeric string
      amount: String(parseAmt(row.amount)),
      poNumber: `CSV-${Date.now()}-${Math.random().toString(36).substr(2,5).toUpperCase()}`,
    }));
    const fresh   = newPayouts.filter((p) => !existingContacts.has(p.data?.email||''));
    const skipped = newPayouts.length - fresh.length;
    setPayouts((prev) => [...prev,...fresh]);
    showSnackbar(skipped>0 ? `Imported ${fresh.length} payee${fresh.length!==1?'s':''}. ${skipped} duplicate${skipped!==1?'s':''} skipped.` : `Imported ${fresh.length} payee${fresh.length!==1?'s':''}!`, skipped>0?'warning':'success');
    setTimeout(() => goToStep(1), 420);
  };

  const handleRemovePayout = (i) => setPayouts((p) => p.filter((_,idx)=>idx!==i));
  const handlePayoutChange = (i,field,val) => setPayouts((p) => p.map((item,idx)=>idx===i?{...item,[field]:val}:item));

  const handleNextStep = () => {
    if (step===0) {
      // FIX: use parseAmt for validation
      if (payouts.length===0||payouts.some((p)=>!p.amount||parseAmt(p.amount)<=0)){showSnackbar('Add at least one payee with a valid amount.','warning');return;}
      if (spendingData.totalAvailable<calculateTotal()){showSnackbar(`Insufficient funds. Need $${calculateTotal().toFixed(2)}, have $${spendingData.totalAvailable.toFixed(2)}.`,'error');return;}
      nextStep();
    } else if (step===1) { nextStep(); }
    else if (step===2) {
      setNotesTouched(true);
      if (!notesValid){showSnackbar(`Describe the payment (at least ${NOTES_MIN_LENGTH} characters).`,'warning');document.getElementById('pay-notes-field')?.focus();return;}
      handlePaymentSubmission();
    }
  };

  // FIX: use parseAmt for canProceed check
  const canProceedFromStep = (s) => { if(s===0)return payouts.length>0&&payouts.every((p)=>p.amount&&parseAmt(p.amount)>0); if(s===1)return true; if(s===2)return notesValid; return false; };

  const handlePaymentSubmission = () => {
    setIsProcessing(true);
    let finalCcEmails=emailRecipients;
    if (!finalCcEmails.length&&userInfo?.email) finalCcEmails=[userInfo.email];
    const sharedPoNumber=getPONumber('TCC');
    // CHANGED: SMS payout type removed — only existing users and email invites are submitted
    const unifiedPayouts=payouts.map((p)=>{
      // FIX: parseAmt ensures commas in stored amounts don't corrupt the API payload
      if(p.type==='existing') return{type:'existing',creator_id:p.data.creator,amount:parseAmt(p.amount),po_number:sharedPoNumber};
      return{type:'email',creator_name:p.data.name,email:p.data.email,amount:parseAmt(p.amount),po_number:sharedPoNumber};
    });
    client.payouts.createBatch({payouts:unifiedPayouts,fee_payer:feePayer,notes,cc_emails:finalCcEmails,payout_method_mode:methodMode,forced_payment_rail:forcedRail||null,rail_fee_payer:railHasFee?railFeePayer:null})
      .then(()=>{window.location.href='/blitzpay';})
      .catch((error)=>{console.error(error);showSnackbar('Payment failed. Please try again.','error');if(error.response?.status===500)setErrorOpen(true);})
      .finally(()=>setIsProcessing(false));
  };

  // Gates
  if (companyInfoLoading||spendingInfoLoading) return (
    <Box sx={{p:{xs:2,md:3},bgcolor:'grey.50',minHeight:'100vh'}}><Container maxWidth="md">
      <Card sx={{p:{xs:2,md:3},borderRadius:3,textAlign:'center'}}><CircularProgress sx={{mb:2}}/><Typography variant="body2" color="text.secondary">Verifying BlitzPay access…</Typography></Card>
    </Container></Box>
  );
  if (companyInfoError||!hasBlitzPayAccess) return (
    <Box sx={{p:{xs:2,md:3},bgcolor:'grey.50',minHeight:'100vh'}}><Container maxWidth="md">
      <Card sx={{p:{xs:3,md:4},borderRadius:3,textAlign:'center'}}>
        <Avatar sx={{width:{xs:80,md:100},height:{xs:80,md:100},bgcolor:'error.main',mx:'auto',mb:3}}><LockIcon sx={{fontSize:{xs:50,md:60}}}/></Avatar>
        <Typography variant="h4" fontWeight="bold" gutterBottom>Access Restricted</Typography>
        <Typography variant="body1" color="text.secondary" sx={{mb:3}}>Your company does not have BlitzPay enabled.</Typography>
        <Alert severity="warning" sx={{mb:3,textAlign:'left'}}><Typography variant="body2" gutterBottom><strong>BlitzPay Required</strong></Typography><Typography variant="body2">Have your administrator enable BlitzPay in team settings.</Typography></Alert>
        <Stack direction={{xs:'column',sm:'row'}} spacing={2} justifyContent="center">
          <Button variant="contained" onClick={()=>navigate('/user')} fullWidth={isMobile}>Go to Team Settings</Button>
          <Button variant="outlined" onClick={()=>navigate('/dashboard')} fullWidth={isMobile}>Return to Dashboard</Button>
        </Stack>
      </Card>
    </Container></Box>
  );

  // ── STEP 0 ──────────────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <Fade in={!isTransitioning} timeout={300}>
      <Box>
        <Box sx={{textAlign:'center',mb:{xs:2.5,md:3.5}}}>
          <Avatar sx={{bgcolor:'primary.main',width:{xs:60,md:72},height:{xs:60,md:72},mx:'auto',mb:{xs:1.5,md:2}}}><Person sx={{fontSize:{xs:30,md:36}}}/></Avatar>
          <Typography variant="h4" sx={{fontWeight:700,mb:0.75,fontSize:{xs:'1.5rem',sm:'1.75rem',md:'2rem'}}}>Who do you want to pay?</Typography>
          <Typography variant="body1" color="text.secondary" sx={{fontSize:{xs:'0.875rem',md:'1rem'}}}>Search by username or email — or bulk-import a CSV</Typography>
        </Box>

        {/* ── SEARCH FIRST ── */}
        <SearchTicker />
        <Box sx={{position:'relative',mb:2}}>
          <TextField fullWidth value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)}
            placeholder={isMobile?'Username or email':'Search by username or email address'}
            size={isMobile?'small':'medium'}
            InputProps={{
              startAdornment:<InputAdornment position="start"><SearchIcon color={isSearching?'primary':'action'} sx={{animation:isSearching?'spin 1.2s linear infinite':'none','@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/></InputAdornment>,
              endAdornment:isSearching?<CircularProgress size={18}/>:null,
            }}/>
          <Collapse in={isSearching}><LinearProgress sx={{mt:0.5,borderRadius:2}}/></Collapse>
        </Box>

        {searchPerformed&&found&&(
          <Box sx={{mb:3}}>
            <Alert severity={exactMatch?'success':'info'} sx={{borderRadius:2,mb:2,fontSize:{xs:'0.8125rem',md:'0.875rem'}}}>{exactMatch?'Exact match found.':`${searchResults.length} match${searchResults.length!==1?'es':''} found.`}</Alert>
            <Stack spacing={2}>
              {searchResults.map((result,idx)=>(
                <Grow in key={result.creator} timeout={150+idx*100}>
                  <Card variant="outlined" sx={{borderRadius:3,transition:'box-shadow 0.2s,border-color 0.2s','&:hover':{boxShadow:3,borderColor:'primary.main'}}}>
                    <CardContent sx={{p:{xs:2,md:2.5}}}>
                      <Box sx={{display:'flex',flexDirection:{xs:'column',sm:'row'},justifyContent:'space-between',alignItems:{xs:'flex-start',sm:'center'},gap:2,mb:1.5}}>
                        <Box sx={{display:'flex',alignItems:'center',flex:1,minWidth:0}}>
                          <Avatar src={result.pfphref} sx={{mr:1.5,width:44,height:44,flexShrink:0}}>{(result.display_name||result.creator||'?')[0].toUpperCase()}</Avatar>
                          <Box sx={{flex:1,minWidth:0}}>
                            <Typography variant="subtitle1" sx={{fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{result.display_name||result.creator}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>@{result.creator}</Typography>
                          </Box>
                        </Box>
                        <Button variant="contained" onClick={()=>handleAddExisting(result)} size={isMobile?'small':'medium'} fullWidth={isMobile} sx={{minWidth:140,flexShrink:0}}>Add to Payouts</Button>
                      </Box>
                      <Stack direction="row" spacing={0.75} sx={{mb:1.5,flexWrap:'wrap',gap:0.5}}>
                        {result.is_verified&&<Chip label="Verified" color="success" size="small"/>}
                        {result.is_vendor&&<Chip label="Vendor" color="primary" size="small"/>}
                        {result.manager&&<Chip label={`Managed by ${result.manager}`} size="small" icon={<BusinessIcon sx={{fontSize:14}}/>} variant="outlined"/>}
                      </Stack>
                      <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',mb:0.75,fontSize:'0.68rem'}}>Payment Methods</Typography>
                      <WalletBadges walletTypes={result.wallet_types} legacyMethods={result.legacy_payment_methods} hasModernWallets={result.has_modern_wallets}/>
                    </CardContent>
                  </Card>
                </Grow>
              ))}
            </Stack>
            <Collapse in={showManualAddPanel} mountOnEnter unmountOnExit>
              <Card variant="outlined" sx={{borderRadius:3,mt:2.5,borderStyle:'dashed'}}>
                <CardContent sx={{p:{xs:2,md:2.5}}}>
                  <Typography variant="subtitle1" sx={{fontWeight:700,mb:0.5}}>Add as new vendor anyway</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb:2,fontSize:'0.85rem'}}>Invite a brand-new vendor by email.</Typography>
                  {searchType==='email'&&<TextField fullWidth label="Email Address" value={debouncedQuery} disabled size="small" sx={{mb:2}} InputProps={{startAdornment:<InputAdornment position="start"><EmailIcon color="action" fontSize="small"/></InputAdornment>}}/>}
                  <Stack spacing={1.5}>
                    <TextField fullWidth label="Vendor Name" value={manualAddName} onChange={(e)=>setManualAddName(e.target.value)} size="small" InputProps={{startAdornment:<InputAdornment position="start"><Person fontSize="small" color="action"/></InputAdornment>}}/>
                    {searchType!=='email'&&<TextField fullWidth label="Email Address" value={manualAddEmail} onChange={(e)=>setManualAddEmail(e.target.value)} size="small" error={!!manualAddEmail&&!isValidEmail(manualAddEmail)} helperText={manualAddEmail&&!isValidEmail(manualAddEmail)?'Invalid email':''} InputProps={{startAdornment:<InputAdornment position="start"><EmailIcon fontSize="small" color="action"/></InputAdornment>}}/>}
                    <TextField fullWidth label="Payment Amount" type="number" value={manualAddAmount} onChange={(e)=>setManualAddAmount(e.target.value)} size="small" InputProps={{startAdornment:<InputAdornment position="start"><AttachMoney fontSize="small" color="action"/></InputAdornment>}}/>
                    <Button variant="contained" onClick={handleManualAddFromQuery} fullWidth={isMobile}>Add to Payouts</Button>
                  </Stack>
                </CardContent>
              </Card>
            </Collapse>
          </Box>
        )}

        <Collapse in={isSearching&&!searchPerformed}>
          <Stack spacing={2} sx={{my:2}}>
            {[0,1].map((i)=>(
              <Card key={i} variant="outlined" sx={{borderRadius:3}}>
                <CardContent sx={{display:'flex',alignItems:'center',p:{xs:2,md:2.5},gap:2}}>
                  <Skeleton variant="circular" width={44} height={44} sx={{flexShrink:0}}/>
                  <Box sx={{flex:1}}><Skeleton variant="text" width="45%"/><Skeleton variant="text" width="28%"/></Box>
                  <Skeleton variant="rectangular" width={130} height={36} sx={{borderRadius:1.5}}/>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Collapse>

        {searchPerformed&&!found&&(
          <Box sx={{my:2}}>
            {searchType==='phone'?(
              <Alert severity="info" sx={{borderRadius:2}}>
                <Typography variant="body2" sx={{fontWeight:700,mb:0.5}}>Phone payouts are no longer supported.</Typography>
                <Typography variant="body2">No match for <strong>"{debouncedQuery}"</strong>. New vendors can only be invited by email — search for their email address or username instead.</Typography>
              </Alert>
            ):(
              <>
                <Alert severity="warning" sx={{borderRadius:2,mb:2}}>No match for <strong>"{debouncedQuery}"</strong>. Add as a new vendor below.</Alert>
                <Card variant="outlined" sx={{borderRadius:3}}>
                  <CardContent sx={{p:{xs:2,md:2.5}}}>
                    <Box sx={{display:'flex',alignItems:'center',gap:1,mb:2}}><GlobalIcon color="primary" sx={{fontSize:18}}/><Typography variant="subtitle2">New Vendor — Email Invite</Typography></Box>
                    <Stack spacing={1.5}>
                      <TextField fullWidth label="Vendor Name" value={emailInviteName} onChange={(e)=>setEmailInviteName(e.target.value)} size="small"/>
                      {searchType==='email'
                        ?<TextField fullWidth label="Email Address" value={debouncedQuery} disabled size="small"/>
                        :<TextField fullWidth label="Email Address" value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} size="small" error={!!inviteEmail&&!isValidEmail(inviteEmail)} helperText={inviteEmail&&!isValidEmail(inviteEmail)?'Invalid email':''}/>}
                      <TextField fullWidth label="Payment Amount" type="number" value={emailInviteAmount} onChange={(e)=>setEmailInviteAmount(e.target.value)} size="small" InputProps={{startAdornment:<InputAdornment position="start">$</InputAdornment>}}/>
                      <Button variant="contained" onClick={handleAddNew} fullWidth={isMobile}>Add to Payouts</Button>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        )}

        {/* ── Divider ── */}
        <Box sx={{display:'flex',alignItems:'center',gap:2,my:3}}>
          <Divider sx={{flex:1}}/>
          <Typography variant="caption" color="text.disabled" sx={{fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',fontSize:'0.68rem',whiteSpace:'nowrap'}}>or import a list</Typography>
          <Divider sx={{flex:1}}/>
        </Box>

        {/* ── CSV BANNER second ── */}
        <CSVBanner onUploadClick={()=>setCSVModalOpen(true)}/>

        {/* ── Payout Queue ── */}
        {payouts.length>0&&(
          <Box sx={{mt:4}}>
            <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',mb:2}}>
              <Typography variant="h6" sx={{fontWeight:700}}>Payout Queue</Typography>
              <Chip label={`${payouts.length} payee${payouts.length!==1?'s':''} · $${calculateSubtotal().toFixed(2)}`} color="primary" size="small" sx={{fontWeight:700}}/>
            </Box>
            <Stack spacing={1.5}>
              {payouts.map((payout,index)=>(
                <Grow in key={index} timeout={150+index*80}>
                  <Card variant="outlined" sx={{borderRadius:3,transition:'box-shadow 0.15s','&:hover':{boxShadow:2}}}>
                    <CardContent sx={{p:{xs:2,md:2.5},'&:last-child':{pb:{xs:2,md:2.5}}}}>
                      <Box sx={{display:'flex',flexDirection:{xs:'column',sm:'row'},alignItems:{xs:'stretch',sm:'center'},gap:2}}>
                        <Box sx={{flex:1,minWidth:0}}>
                          {payout.type==='existing'?(
                            <>
                              <Box sx={{display:'flex',alignItems:'center',mb:1}}>
                                <Avatar src={payout.data.pfphref} sx={{mr:1.5,width:40,height:40,flexShrink:0}}>{(payout.data.display_name||payout.data.creator||'?')[0].toUpperCase()}</Avatar>
                                <Box sx={{minWidth:0}}>
                                  <Typography variant="subtitle2" sx={{fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{payout.data.display_name||payout.data.creator}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{display:'block'}}>@{payout.data.creator}</Typography>
                                </Box>
                              </Box>
                              <WalletBadges walletTypes={payout.data.wallet_types} legacyMethods={payout.data.legacy_payment_methods} hasModernWallets={payout.data.has_modern_wallets}/>
                            </>
                          ):(
                            <>
                              <Box sx={{display:'flex',alignItems:'center',gap:0.75,mb:0.25}}>
                                <Typography variant="subtitle2" sx={{fontWeight:700}}>{payout.data.name}</Typography>
                                {payout.poNumber?.startsWith('CSV-')&&<Chip label="CSV" size="small" icon={<TableChartIcon sx={{fontSize:'10px !important'}}/>} sx={{fontSize:'0.62rem',height:17,fontWeight:700,bgcolor:alpha(theme.palette.info.main,0.1),color:'info.main',border:`1px solid ${alpha(theme.palette.info.main,0.2)}`}}/>}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{display:'block',mb:0.75,wordBreak:'break-word'}}>{payout.data.email}</Typography>
                              <Chip label="Pending invite to set up payment" size="small" icon={<InfoIcon sx={{fontSize:13}}/>} sx={{fontSize:'0.68rem',height:20,bgcolor:'warning.50',color:'warning.900',border:'1px solid',borderColor:'warning.200'}}/>
                            </>
                          )}
                        </Box>
                        <Box sx={{display:'flex',alignItems:'center',gap:1,flexShrink:0,width:{xs:'100%',sm:'auto'}}}>
                          <TextField label="Amount" type="number" value={payout.amount} onChange={(e)=>handlePayoutChange(index,'amount',e.target.value)} size="small"
                            InputProps={{startAdornment:<InputAdornment position="start"><Typography variant="body2" fontWeight={700}>$</Typography></InputAdornment>}}
                            sx={{width:{xs:'100%',sm:130}}}/>
                          <Tooltip title="Remove payee">
                            <IconButton color="error" size="small" onClick={()=>handleRemovePayout(index)} sx={{bgcolor:'error.50',border:'1px solid',borderColor:'error.100','&:hover':{bgcolor:'error.100'},flexShrink:0}}>
                              <Delete fontSize="small"/>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      {payout.poNumber&&<Typography variant="caption" color="text.disabled" sx={{display:'block',mt:1.25,fontSize:'0.68rem'}}>PO: {payout.poNumber}</Typography>}
                    </CardContent>
                  </Card>
                </Grow>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Fade>
  );

  // ── STEP 1 ──────────────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <Fade in={!isTransitioning} timeout={300}>
      <Box>
        <Box sx={{textAlign:'center',mb:{xs:3,md:4}}}>
          <Avatar sx={{bgcolor:'warning.main',width:{xs:60,md:72},height:{xs:60,md:72},mx:'auto',mb:{xs:1.5,md:2}}}><Payment sx={{fontSize:{xs:30,md:36}}}/></Avatar>
          <Typography variant="h4" sx={{fontWeight:700,mb:0.75,fontSize:{xs:'1.5rem',sm:'1.75rem',md:'2rem'}}}>Who Pays the Fee?</Typography>
          <Typography variant="body1" color="text.secondary" sx={{fontSize:{xs:'0.875rem',md:'1rem'}}}>Choose who covers the Blitz processing cost</Typography>
        </Box>

        <Box sx={{textAlign:'center',mb:3.5}}>
          <Box sx={{display:'inline-flex',flexDirection:'column',alignItems:'center',px:4,py:2.5,borderRadius:3,bgcolor:alpha(theme.palette.primary.main,0.06),border:'2px solid',borderColor:alpha(theme.palette.primary.main,0.18)}}>
            <Typography variant="caption" sx={{fontWeight:700,color:'primary.main',textTransform:'uppercase',letterSpacing:'0.08em',mb:0.5,fontSize:'0.7rem'}}>Your BlitzPay Rate</Typography>
            <Typography variant="h2" sx={{fontWeight:900,color:'primary.main',lineHeight:1,letterSpacing:'-0.03em'}}>{nominalInterestRate}%</Typography>
            <Typography variant="caption" color="text.secondary" sx={{mt:0.5}}>set by your {spendingData.isTeamSource?'team':'company'}</Typography>
          </Box>
        </Box>

        <Box sx={{mb:3}}>
          <Typography variant="overline" sx={{fontWeight:700,color:'text.secondary',mb:1.5,display:'block',fontSize:'0.7rem',letterSpacing:'0.08em'}}>Who covers the BlitzPay fee?</Typography>
          <FeePayerCards value={feePayer} onChange={setFeePayer} meLabel="I'll cover the fee" meSub="Recipients get the full amount — fee added to your total" creatorLabel="Recipients cover the fee" creatorSub="Fee deducted from each payout — they receive less" accentColor="primary"/>
        </Box>

        <Box sx={{borderRadius:2.5,border:'1.5px solid',borderColor:showAdvancedRail?(forcedRail?alpha(theme.palette.primary.main,0.4):'grey.300'):'grey.200',overflow:'hidden',transition:'border-color 0.2s',mb:3}}>
          <Box onClick={()=>setShowAdvancedRail((p)=>!p)} sx={{display:'flex',alignItems:'center',justifyContent:'space-between',px:2.5,py:1.75,cursor:'pointer',userSelect:'none',bgcolor:showAdvancedRail?'grey.50':'transparent','&:hover':{bgcolor:'grey.50'}}}>
            <Box sx={{display:'flex',alignItems:'center',gap:1.25}}>
              <TuneIcon sx={{fontSize:17,color:forcedRail?'primary.main':'text.secondary'}}/>
              <Box>
                <Typography variant="subtitle2" sx={{fontWeight:700,fontSize:'0.88rem',color:forcedRail?'primary.main':'text.primary'}}>Advanced: Lock payment rail</Typography>
                <Typography variant="caption" color="text.secondary" sx={{fontSize:'0.73rem'}}>{forcedRail?`Locked to ${getRailById(forcedRail).label}`:'By default, vendors pick their preferred method'}</Typography>
              </Box>
            </Box>
            <Box sx={{display:'flex',alignItems:'center',gap:1}}>
              {forcedRail&&<Chip label={getRailById(forcedRail).label} size="small" sx={{fontSize:'0.7rem',height:20,fontWeight:700,bgcolor:getRailById(forcedRail).bgColor,color:getRailById(forcedRail).color,border:`1px solid ${getRailById(forcedRail).borderColor}`}}/>}
              {showAdvancedRail?<ExpandLessIcon sx={{fontSize:19,color:'text.disabled'}}/>:<ExpandMoreIcon sx={{fontSize:19,color:'text.disabled'}}/>}
            </Box>
          </Box>
          <Collapse in={showAdvancedRail} mountOnEnter>
            <Box sx={{borderTop:'1px solid',borderColor:'grey.200',p:2.5}}>
              <Typography variant="body2" color="text.secondary" sx={{mb:2.5,fontSize:'0.83rem',lineHeight:1.6}}>Lock all vendors to one specific rail.{!allPayeesAreExistingUsers&&<Box component="span" sx={{display:'block',mt:1,color:'warning.dark',fontWeight:600}}>⚠ Some payees are new vendors — only PayPal available without a platform account.</Box>}</Typography>
              <Typography variant="overline" sx={{fontWeight:700,color:'text.secondary',fontSize:'0.68rem',letterSpacing:'0.08em',display:'block',mb:1.25}}>1 · Select platform</Typography>
              <Card variant="outlined" onClick={()=>setForcedRail(null)} sx={{borderRadius:2.5,cursor:'pointer',mb:1,borderColor:forcedRail===null?'primary.main':'grey.200',borderWidth:forcedRail===null?2:1,bgcolor:forcedRail===null?alpha(theme.palette.primary.main,0.04):'transparent',transition:'all 0.15s','&:hover':{borderColor:forcedRail===null?'primary.main':'grey.400',bgcolor:'grey.50'}}}>
                <CardContent sx={{p:'11px 14px !important',display:'flex',alignItems:'center',gap:1.5}}>
                  <Box sx={{width:34,height:34,borderRadius:1.5,bgcolor:forcedRail===null?'primary.main':'grey.100',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.15s'}}><People sx={{fontSize:17,color:forcedRail===null?'white':'text.secondary'}}/></Box>
                  <Box sx={{flex:1}}>
                    <Typography variant="subtitle2" sx={{fontWeight:700,color:forcedRail===null?'primary.main':'text.primary',fontSize:'0.85rem'}}>Recipient chooses (default)</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{fontSize:'0.72rem'}}>{allPayeesAreExistingUsers?'PayPal, Stripe, Wise, ACH, or USDC — vendor picks':'PayPal only for new vendors; platform users can also choose Stripe, Wise, ACH, or USDC'}</Typography>
                  </Box>
                  {forcedRail===null&&<CheckCircle sx={{color:'primary.main',fontSize:19,flexShrink:0}}/>}
                </CardContent>
              </Card>
              <Stack spacing={1}>
                {availableRails.map((rail)=>{
                  const isSel=forcedRail===rail.id;
                  return(
                    <Card key={rail.id} variant="outlined" onClick={()=>setForcedRail(rail.id)} sx={{borderRadius:2.5,cursor:'pointer',borderColor:isSel?rail.color:'grey.200',borderWidth:isSel?2:1,bgcolor:isSel?rail.bgColor:'transparent',transition:'all 0.15s','&:hover':{borderColor:isSel?rail.color:'grey.400',bgcolor:isSel?rail.bgColor:'grey.50'}}}>
                      <CardContent sx={{p:'11px 14px !important',display:'flex',alignItems:'center',gap:1.5}}>
                        <Box sx={{width:34,height:34,borderRadius:1.5,bgcolor:isSel?rail.color:'grey.100',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.15s'}}><rail.Icon sx={{fontSize:17,color:isSel?'white':'text.secondary'}}/></Box>
                        <Box sx={{flex:1,minWidth:0}}>
                          <Box sx={{display:'flex',alignItems:'center',gap:0.75,flexWrap:'wrap'}}>
                            <Typography variant="subtitle2" sx={{fontWeight:700,color:isSel?rail.color:'text.primary',fontSize:'0.85rem'}}>{rail.label}</Typography>
                            <Chip label={rail.eta} size="small" icon={rail.eta==='Instant'?<BoltIcon sx={{fontSize:'11px !important'}}/>:null} sx={{fontSize:'0.62rem',height:17,fontWeight:700,bgcolor:isSel?alpha(rail.color,0.12):(rail.eta==='Instant'?'success.50':'grey.100'),color:isSel?rail.color:(rail.eta==='Instant'?'success.800':'text.secondary'),border:`1px solid ${isSel?alpha(rail.color,0.25):(rail.eta==='Instant'?'#bbf7d0':'#e0e0e0')}`}}/>
                            {rail.badge&&<Chip label={rail.badge} size="small" sx={{fontSize:'0.62rem',height:17,fontWeight:700,bgcolor:isSel?alpha(rail.color,0.1):'primary.50',color:isSel?rail.color:'primary.700',border:`1px solid ${isSel?alpha(rail.color,0.2):'#bfdbfe'}`}}/>}
                            {rail.feeLabel?<Chip label={rail.feeLabel} size="small" sx={{fontSize:'0.62rem',height:17,fontWeight:700,bgcolor:isSel?alpha(rail.color,0.1):'warning.50',color:isSel?rail.color:'warning.800',border:`1px solid ${isSel?alpha(rail.color,0.25):'#fde68a'}`}}/>:<Chip label="No fee" size="small" sx={{fontSize:'0.62rem',height:17,fontWeight:700,bgcolor:isSel?alpha('#16a34a',0.1):'success.50',color:isSel?'#15803d':'success.700',border:`1px solid ${isSel?alpha('#16a34a',0.2):'#bbf7d0'}`}}/>}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{fontSize:'0.72rem',display:'block',mt:0.2}}>{rail.notes}</Typography>
                        </Box>
                        {isSel&&<CheckCircle sx={{color:rail.color,fontSize:19,flexShrink:0}}/>}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
              <Collapse in={railHasFee} mountOnEnter unmountOnExit>
                <Box sx={{mt:2.5,pt:2.5,borderTop:'1.5px dashed',borderColor:alpha(theme.palette.warning.main,0.35)}}>
                  <Typography variant="overline" sx={{fontWeight:700,color:'warning.dark',fontSize:'0.68rem',letterSpacing:'0.08em',display:'block',mb:1.25}}>2 · Who covers the {selectedRail?.label} transfer fee ({selectedRail?.feePercent}%)?</Typography>
                  <FeePayerCards value={railFeePayer} onChange={setRailFeePayer} meLabel="I'll cover it" meSub="Added to your total — recipient gets the full payout" creatorLabel="Recipient covers it" creatorSub={`Deducted from their payout — they receive less by ${selectedRail?.feePercent}%`} accentColor="warning"/>
                </Box>
              </Collapse>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{p:{xs:2,md:2.5},borderRadius:2.5,bgcolor:alpha(theme.palette.primary.main,0.04),border:'1.5px solid',borderColor:alpha(theme.palette.primary.main,0.18)}}>
          <Typography variant="overline" sx={{fontWeight:700,color:'primary.main',fontSize:'0.68rem',letterSpacing:'0.1em',display:'block',mb:1.5}}>Summary</Typography>
          <Stack spacing={1.25}>
            <Box sx={{display:'flex',justifyContent:'space-between'}}><Typography variant="body2" color="text.secondary">Payouts subtotal</Typography><Typography variant="body2" fontWeight={700}>${calculateSubtotal().toFixed(2)}</Typography></Box>
            <Box sx={{display:'flex',justifyContent:'space-between'}}><Typography variant="body2" color="text.secondary">BlitzPay fee ({nominalInterestRate}%)<Typography component="span" variant="caption" sx={{ml:0.5,color:'text.disabled'}}>· {feePayer==='me'?'you cover':'from recipients'}</Typography></Typography><Typography variant="body2" fontWeight={700}>${calculateBlitzPayFee(calculateSubtotal()).toFixed(2)}</Typography></Box>
            {railHasFee&&<Box sx={{display:'flex',justifyContent:'space-between'}}><Typography variant="body2" color="text.secondary">{selectedRail.label} fee ({selectedRail.feePercent}%)<Typography component="span" variant="caption" sx={{ml:0.5,color:'text.disabled'}}>· {railFeePayer==='me'?'you cover':'from recipients'}</Typography></Typography><Typography variant="body2" fontWeight={700} color="warning.main">${railFeeAmount.toFixed(2)}</Typography></Box>}
            <Divider sx={{my:0.5}}/>
            <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}}><Typography variant="subtitle2" fontWeight={800}>You pay</Typography><Typography variant="subtitle1" fontWeight={900} color="primary">${calculateTotal().toFixed(2)}</Typography></Box>
            <Box sx={{display:'flex',justifyContent:'space-between'}}><Typography variant="body2" color="text.secondary">Recipients receive</Typography><Typography variant="body2" fontWeight={700} color="success.main">${calculateReceiverAmount().toFixed(2)}</Typography></Box>
          </Stack>
        </Box>
      </Box>
    </Fade>
  );

  // ── STEP 2 ──────────────────────────────────────────────────────────────────
  const renderStep2 = () => (
    <Fade in={!isTransitioning} timeout={300}>
      <Box>
        <Box sx={{textAlign:'center',mb:{xs:2.5,md:4}}}>
          <Avatar sx={{bgcolor:'success.main',width:{xs:60,md:72},height:{xs:60,md:72},mx:'auto',mb:{xs:1.5,md:2}}}><CheckCircle sx={{fontSize:{xs:30,md:36}}}/></Avatar>
          <Typography variant="h4" sx={{fontWeight:700,mb:0.75,fontSize:{xs:'1.5rem',sm:'1.75rem',md:'2rem'}}}>Confirm Your Payment</Typography>
          <Typography variant="body1" color="text.secondary" sx={{fontSize:{xs:'0.875rem',md:'1rem'}}}>Review everything before sending</Typography>
        </Box>

        <SectionCard icon={<People sx={{fontSize:20}}/>} title="Payout Recipients">
          <Stack divider={<Divider/>} spacing={0}>
            {payouts.map((payout,index)=>(
              <Box key={index} sx={{display:'flex',flexDirection:{xs:'column',sm:'row'},justifyContent:'space-between',alignItems:{xs:'flex-start',sm:'center'},py:1.75,gap:1}}>
                <Box sx={{flex:1,minWidth:0}}>
                  {payout.type==='existing'?(
                    <Box sx={{display:'flex',alignItems:'center',gap:1.5}}>
                      <Avatar src={payout.data.pfphref} sx={{width:36,height:36,flexShrink:0}}>{(payout.data.display_name||payout.data.creator||'?')[0].toUpperCase()}</Avatar>
                      <Box sx={{minWidth:0}}><Typography variant="subtitle2" sx={{fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{payout.data.display_name||payout.data.creator}</Typography><Typography variant="caption" color="text.secondary">@{payout.data.creator}</Typography></Box>
                    </Box>
                  ):(
                    <>
                      <Box sx={{display:'flex',alignItems:'center',gap:0.75}}>
                        <Typography variant="subtitle2" sx={{fontWeight:700}}>{payout.data.name}</Typography>
                        {payout.poNumber?.startsWith('CSV-')&&<Chip label="CSV" size="small" icon={<TableChartIcon sx={{fontSize:'10px !important'}}/>} sx={{fontSize:'0.62rem',height:17,fontWeight:700,bgcolor:alpha(theme.palette.info.main,0.1),color:'info.main',border:`1px solid ${alpha(theme.palette.info.main,0.2)}`}}/>}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{wordBreak:'break-word'}}>{payout.data.email} · New vendor</Typography>
                    </>
                  )}
                </Box>
                {/* FIX: use parseAmt so display is correct even if stored value had a comma */}
                <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{whiteSpace:'nowrap',flexShrink:0}}>${parseAmt(payout.amount||0).toFixed(2)}</Typography>
              </Box>
            ))}
          </Stack>
        </SectionCard>

        <SectionCard icon={<TuneIcon sx={{fontSize:20}}/>} title="Payment Method">
          <Box sx={{display:'flex',alignItems:'center',gap:1.5}}>
            {forcedRail?(
              <><Box sx={{width:32,height:32,borderRadius:1.5,bgcolor:selectedRail.bgColor,display:'flex',alignItems:'center',justifyContent:'center',border:`1px solid ${selectedRail.borderColor}`,flexShrink:0}}><selectedRail.Icon sx={{fontSize:17,color:selectedRail.color}}/></Box><Box><Typography variant="subtitle2" fontWeight={700}>{selectedRail.label} — Locked</Typography><Typography variant="caption" color="text.secondary">Vendors directed straight to connect {selectedRail.label}</Typography></Box></>
            ):(
              <><Box sx={{display:'flex',gap:0.5,flexShrink:0}}>{availableRails.slice(0,4).map((r)=><Box key={r.id} sx={{width:24,height:24,borderRadius:1,bgcolor:r.bgColor,display:'flex',alignItems:'center',justifyContent:'center',border:`1px solid ${r.borderColor}`}}><r.Icon sx={{fontSize:13,color:r.color}}/></Box>)}</Box><Box><Typography variant="subtitle2" fontWeight={700}>Recipient Chooses</Typography><Typography variant="caption" color="text.secondary">{allPayeesAreExistingUsers?'PayPal, Stripe, Wise, ACH, or USDC':'PayPal available to all; platform users can also choose Stripe, Wise, ACH, or USDC'}</Typography></Box></>
            )}
          </Box>
        </SectionCard>

        <SectionCard icon={<AttachMoney sx={{fontSize:20}}/>} title="Financial Summary" accent>
          <Stack spacing={1.25}>
            {[
              {label:'Payouts total',value:`$${calculateSubtotal().toFixed(2)}`},
              {label:`BlitzPay fee (${nominalInterestRate}%) · ${feePayer==='me'?'you cover':'from recipients'}`,value:`$${calculateBlitzPayFee(calculateSubtotal()).toFixed(2)}`},
              ...(railHasFee?[{label:`${selectedRail.label} transfer fee (${selectedRail.feePercent}%) · ${railFeePayer==='me'?'you cover':'from recipients'}`,value:`$${railFeeAmount.toFixed(2)}`,accent:true}]:[]),
              {label:'Recipients will receive',value:`$${calculateReceiverAmount().toFixed(2)}`,green:true},
            ].map((row)=>(
              <Box key={row.label} sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                <Typography variant="body2" sx={{fontWeight:700,color:row.green?'success.main':row.accent?'warning.main':'text.primary'}}>{row.value}</Typography>
              </Box>
            ))}
            <Divider sx={{my:0.5}}/>
            <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',pt:0.5}}>
              <Typography variant="subtitle1" fontWeight={800}>You Pay</Typography>
              <Typography variant="h6" fontWeight={900} color="primary" sx={{letterSpacing:'-0.02em'}}>${calculateTotal().toFixed(2)}</Typography>
            </Box>
          </Stack>
          <Box sx={{mt:2.5,pt:2.5,borderTop:'1px solid',borderColor:'primary.100'}}>
            <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.05em',fontSize:'0.65rem',display:'block',mb:1.5}}>Spending Power After Payment</Typography>
            <Box sx={{display:'flex',gap:2,mb:2}}>
              {[{label:'Now',val:spendingData.totalAvailable,color:'text.primary'},{label:'After',val:spendingData.totalAvailable-calculateTotal(),color:(spendingData.totalAvailable-calculateTotal())<0?'error.main':(spendingData.totalAvailable-calculateTotal())<1000?'warning.main':'success.main'}].map(({label,val,color})=>(
                <Box key={label} sx={{flex:1,textAlign:'center',p:1.5,borderRadius:2,bgcolor:'white',border:'1px solid',borderColor:'grey.200'}}>
                  <Typography variant="caption" color="text.secondary" sx={{display:'block',mb:0.25}}>{label}</Typography>
                  <Typography variant="subtitle2" fontWeight={800} color={color}>${val.toFixed(2)}</Typography>
                </Box>
              ))}
            </Box>
            {spendingData.isTeamSource&&spendingData.effectiveLimit>0&&(<><Box sx={{display:'flex',justifyContent:'space-between',mb:0.75}}><Typography variant="caption" color="text.secondary">Team utilization after</Typography><Typography variant="caption" fontWeight={700}>{((spendingData.teamUsed+calculateTotal())/spendingData.effectiveLimit*100).toFixed(1)}%</Typography></Box><LinearProgress variant="determinate" value={Math.min(((spendingData.teamUsed+calculateTotal())/spendingData.effectiveLimit*100),100)} color={getUtilizationColor((spendingData.teamUsed+calculateTotal())/spendingData.effectiveLimit*100)} sx={{height:6,borderRadius:3}}/></>)}
            {spendingData.totalAvailable<calculateTotal()&&<Alert severity="error" sx={{mt:2,borderRadius:2}} icon={<ErrorOutlineIcon/>}><Typography variant="body2" fontWeight={600}>Insufficient funds — need ${calculateTotal().toFixed(2)}, have ${spendingData.totalAvailable.toFixed(2)}.</Typography></Alert>}
          </Box>
        </SectionCard>

        <SectionCard icon={<Email sx={{fontSize:20}}/>} title="Email Notifications">
          {userInfo?.email&&<Box sx={{mb:2,p:1.5,borderRadius:1.5,bgcolor:'info.50',border:'1px solid',borderColor:'info.100'}}><Typography variant="caption" sx={{display:'flex',alignItems:'center',gap:0.5,color:'info.main'}}><InfoIcon sx={{fontSize:14}}/>Default: <strong>{userInfo.email}</strong></Typography></Box>}
          <Autocomplete multiple freeSolo options={companyUsers} value={emailRecipients} onChange={(e,v)=>setEmailRecipients(v)} size={isMobile?'small':'medium'}
            inputValue={emailSearchInput} onInputChange={(ev,val,reason)=>{if(reason!=='reset')setEmailSearchInput(val);}} loading={isEmailSearching} filterOptions={(o)=>o}
            renderInput={(params)=><TextField {...params} label="Additional Email Recipients" placeholder="Type at least 2 characters to search users"/>}
            renderTags={(value,getTagProps)=>value.map((option,index)=><Chip variant="outlined" label={option} {...getTagProps({index})} key={index} size={isMobile?'small':'medium'}/>)}/>
        </SectionCard>

        <SectionCard icon={<EditNoteIcon sx={{fontSize:20}}/>}
          title={<Box sx={{display:'flex',alignItems:'center',gap:1}}>Purpose / Notes<Chip label="Required" size="small" sx={{fontSize:'0.65rem',height:18,fontWeight:700,bgcolor:'error.50',color:'error.main',border:'1px solid',borderColor:'error.200'}}/></Box>}
          sx={{borderColor:notesInvalid?'error.300':(notesValid&&notesTouched?'success.300':undefined),bgcolor:notesInvalid?'error.50':undefined}}>
          <Typography variant="body2" color="text.secondary" sx={{mb:2,fontSize:'0.85rem'}}>Briefly describe what this payment is for — required for your records and compliance.</Typography>
          <TextField id="pay-notes-field" label="What is this payment for?" fullWidth multiline rows={isMobile?3:4}
            value={notes} onChange={(e)=>{setNotes(e.target.value);if(!notesTouched)setNotesTouched(true);}} onBlur={()=>setNotesTouched(true)}
            required error={notesInvalid}
            helperText={notesInvalid?`Add at least ${NOTES_MIN_LENGTH} characters (${notes.trim().length}/${NOTES_MIN_LENGTH})`:(notesValid&&notesTouched)?'✓ Looks good':`${notes.trim().length}/${NOTES_MIN_LENGTH} characters minimum`}
            placeholder="e.g. Campaign deliverables for Q2 Instagram series, invoice #INV-2024-042"
            FormHelperTextProps={{sx:{color:notesInvalid?'error.main':(notesValid&&notesTouched)?'success.main':'text.secondary',fontWeight:600}}}
            sx={{'& .MuiOutlinedInput-root':{'& fieldset':{borderColor:notesInvalid?'error.main':(notesValid&&notesTouched)?'success.main':undefined},'&:hover fieldset':{borderColor:notesInvalid?'error.main':'primary.main'},'&.Mui-focused fieldset':{borderColor:notesInvalid?'error.main':'primary.main'}}}}/>
        </SectionCard>
      </Box>
    </Fade>
  );

  const APPSHELL_HEIGHT = 86;
  const renderStepContent = () => [renderStep0,renderStep1,renderStep2][step]?.()||null;

  return (
    <Box sx={{maxWidth:1000,mx:'auto',p:{xs:1.5,sm:2,md:3},pb:{xs:`${APPSHELL_HEIGHT+80}px`,sm:3}}}>
      <Box sx={{textAlign:'center',mb:{xs:2,md:4}}}>
        <Typography variant="h3" sx={{fontWeight:800,mb:0.75,fontSize:{xs:'1.75rem',sm:'2.25rem',md:'3rem'},letterSpacing:'-0.02em'}}>Send a Blitz Pay</Typography>
        <Typography variant="body1" color="text.secondary" sx={{fontSize:{xs:'0.875rem',sm:'1rem'}}}>Pay vendors and creators quickly and securely</Typography>
      </Box>

      <Stepper activeStep={step} alternativeLabel={isMobile} sx={{mb:{xs:2,md:4},'& .MuiStepLabel-label':{fontSize:{xs:'0.75rem',sm:'0.875rem',md:'1rem'}}}}>
        {steps.map((s)=>(
          <Step key={s.label}>
            <StepLabel StepIconComponent={({active,completed})=>(
              <Avatar sx={{width:{xs:32,md:40},height:{xs:32,md:40},bgcolor:completed?'success.main':active?'primary.main':'grey.300',color:completed||active?'white':'grey.600',transition:'all 0.3s'}}>
                {React.cloneElement(completed?<CheckCircle/>:s.icon,{sx:{fontSize:{xs:18,md:22}}})}
              </Avatar>
            )}><Typography sx={{fontWeight:600,fontSize:{xs:'0.75rem',sm:'0.875rem',md:'1rem'}}}>{s.label}</Typography></StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={0} sx={{p:{xs:2,sm:3,md:4},borderRadius:3,border:'1px solid',borderColor:'grey.200',minHeight:{xs:300,md:400},mb:{xs:2,md:0}}}>
        {renderStepContent()}
      </Paper>

      <Box sx={{display:'flex',justifyContent:'space-between',gap:2,mt:{xs:2,md:3},position:{xs:'fixed',sm:'relative'},bottom:{xs:`${APPSHELL_HEIGHT}px`,sm:'auto'},left:{xs:0,sm:'auto'},right:{xs:0,sm:'auto'},p:{xs:2,sm:0},bgcolor:{xs:'background.paper',sm:'transparent'},borderTop:{xs:1,sm:0},borderColor:{xs:'divider',sm:'transparent'},zIndex:{xs:1000,sm:'auto'},boxShadow:{xs:'0 -2px 12px rgba(0,0,0,0.08)',sm:'none'}}}>
        <Button variant="outlined" startIcon={<ArrowBack/>} onClick={prevStep} disabled={step===0||isProcessing} sx={{minWidth:120,py:1.5,borderRadius:2}}>Back</Button>
        {step<steps.length-1?(
          <Button variant="contained" size="large" endIcon={<ArrowForward/>} onClick={handleNextStep} disabled={!canProceedFromStep(step)||isProcessing} sx={{minWidth:130,py:1.5,borderRadius:2,fontWeight:700}}>Continue</Button>
        ):(
          <Button variant="contained" size="large" color="success" onClick={handleNextStep} disabled={isProcessing||!canProceedFromStep(step)||spendingData.totalAvailable<calculateTotal()} startIcon={isProcessing?<CircularProgress size={20} color="inherit"/>:<CheckCircle/>} sx={{minWidth:160,py:1.5,borderRadius:2,fontWeight:700}}>
            {isProcessing?'Processing…':'Confirm & Pay'}
          </Button>
        )}
      </Box>

      <CSVUploadModal open={csvModalOpen} onClose={()=>setCSVModalOpen(false)} onImportAndAdvance={handleCSVImportAndAdvance} showSnackbar={showSnackbar}/>

      <Backdrop open={isProcessing} sx={{color:'#fff',zIndex:1300}}>
        <Box sx={{textAlign:'center'}}>
          <CircularProgress color="inherit" size={isMobile?50:60} thickness={4}/>
          <Typography variant="h6" sx={{mt:2.5,color:'white',fontWeight:700}}>Processing your Blitz Pay…</Typography>
          <Typography variant="body2" sx={{mt:0.5,color:'rgba(255,255,255,0.75)'}}>Please don't close this page</Typography>
        </Box>
      </Backdrop>

      <Snackbar open={errorOpen} autoHideDuration={6000} onClose={()=>setErrorOpen(false)} anchorOrigin={{vertical:'top',horizontal:'center'}}>
        <AlertComponent onClose={()=>setErrorOpen(false)} severity="error">Payment failed — please try again later.</AlertComponent>
      </Snackbar>
      <Snackbar open={snackbarOpen} autoHideDuration={5500} onClose={()=>setSnackbarOpen(false)} anchorOrigin={{vertical:'top',horizontal:'center'}}>
        <AlertComponent onClose={()=>setSnackbarOpen(false)} severity={snackbarSeverity}>{snackbarMessage}</AlertComponent>
      </Snackbar>
    </Box>
  );
};

export default PayPage;